import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card, CardContent, Grid, Typography, Box, Chip, LinearProgress, Alert, Button, Divider, Snackbar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { apiService } from "../services/api";
import { carePlanService, carePlanErrorMessage } from "../services/carePlanService";
import AddActivityDialog from "./care-plan/AddActivityDialog";

const MEASUREMENT_LABELS = {
  WEIGHT: "Weight",
  BLOOD_PRESSURE: "Blood pressure",
  HEART_RATE: "Heart rate",
  TEMPERATURE: "Temperature",
  OXYGEN_SATURATION: "Oxygen saturation",
};
const WEEKDAYS = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Same status, two voices: the care team gets the plain clinical label; the
// patient gets wording that doesn't read as a grade being handed down.
const STATUS = {
  ON_TRACK: { color: "success", bar: "success", provider: "On track", patient: "On track" },
  NEEDS_ATTENTION: { color: "warning", bar: "warning", provider: "Needs attention", patient: "Almost there" },
  OFF_TRACK: { color: "error", bar: "error", provider: "Off track", patient: "Let's catch up" },
  NOT_ENOUGH_DATA: { color: "default", bar: "inherit", provider: "Not enough data yet", patient: "Just getting started" },
  NOT_STARTED: { color: "default", bar: "inherit", provider: "Plan hasn't started", patient: "Starts soon" },
  NO_MONITORING: { color: "default", bar: "inherit", provider: "No readings required", patient: "No readings required" },
};

// Dates arrive as plain YYYY-MM-DD; compare them as calendar days so the
// browser's own timezone can't shift the answer by one.
const dayNumber = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return Date.UTC(y, m - 1, d) / 86400000;
};
const fmtDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
};

const lastCheckInText = (last, today) => {
  if (!last) return "No check-ins yet";
  const diff = dayNumber(today) - dayNumber(last);
  if (diff <= 0) return "Last check-in: today";
  if (diff === 1) return "Last check-in: yesterday";
  return `Last check-in: ${diff} days ago`;
};

const reviewText = (days) => {
  if (days === null || days === undefined) return { text: "No review date set", color: "text.secondary" };
  if (days < 0) return { text: `Review overdue by ${-days} day${days === -1 ? "" : "s"}`, color: "error.main" };
  if (days === 0) return { text: "Review due today", color: "warning.main" };
  return { text: `Next review in ${days} day${days === 1 ? "" : "s"}`, color: "text.secondary" };
};

const listNames = (names) =>
  names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/**
 * Adherence to the active care plan, shown at the top of the patient profile —
 * once in the patient's own view, once in the care team's view. Everything it
 * shows is computed server-side (see CarePlanAdherenceService).
 *
 * In the PATIENT's view it is also where they act: mark their own tasks done, and
 * log extra activities ("Ran an extra mile"). Extras are shown but never counted in
 * the score. showTasks / showLink let the My Care Plan page, which already has the
 * full task list, hide the duplicates.
 *
 * It never gets in the way: while loading, on any error, or when a patient has no
 * active plan, the patient's view renders nothing (an empty box is just noise to
 * them). The care team sees a one-line note when there's no plan.
 */
export default function CarePlanAdherenceBanner({ patientId, audience = "provider", showLink = true, showTasks = true }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const isPatient = audience === "patient";

  const load = useCallback(() => {
    if (!patientId || patientId === "new") return Promise.resolve();
    return apiService
      .getCarePlanAdherence(patientId)
      .then(setData)
      .catch((err) => console.warn("Could not load care plan adherence:", err));
  }, [patientId]);

  useEffect(() => {
    load();
    // Anything that changes the plan (the Care Plan tab, My Care Plan, this banner)
    // announces it, so every copy on screen stays current without wiring them together.
    window.addEventListener("careplan:changed", load);
    return () => window.removeEventListener("careplan:changed", load);
  }, [load]);

  const announce = () => window.dispatchEvent(new Event("careplan:changed"));

  const completeTask = async (task) => {
    setBusyId(task.id);
    setError(null);
    try {
      await carePlanService.updateTask(patientId, data.planId, task.id, { status: "DONE" });
      setNotice("Nice work — marked done");
      announce();
    } catch (err) {
      setError(carePlanErrorMessage(err, "That didn't save. Please try again."));
    } finally {
      setBusyId(null);
    }
  };

  const addActivity = async (payload) => {
    setSaving(true);
    setError(null);
    try {
      await carePlanService.addTask(patientId, data.planId, payload);
      setAddOpen(false);
      setNotice(payload.alreadyDone ? "Logged — nice!" : "Added to your to-do list");
      announce();
    } catch (err) {
      setError(carePlanErrorMessage(err, "That didn't save. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;

  if (!data.hasActivePlan) {
    return isPatient ? null : (
      <Alert severity="info" sx={{ mb: 3 }}>
        No active care plan for this patient.
      </Alert>
    );
  }

  const style = STATUS[data.status] || STATUS.NOT_ENOUGH_DATA;
  const review = reviewText(data.daysUntilReview);
  const dueTodayNotDone = data.items.filter((i) => i.dueToday && !i.doneToday);
  // Tolerate a frontend deployed a moment before the backend that sends these.
  const openTasks = data.openTasks || [];
  const extras = data.extraActivities || 0;
  const extrasLabel = `${extras} extra ${extras === 1 ? "activity" : "activities"}`;

  return (
    <>
      <Card sx={{ mb: 3 }} elevation={3}>
        <CardContent>
          <Grid container spacing={3}>
            {/* Overall monitoring adherence */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography variant="overline" color="text.secondary">
                Care plan adherence
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                {data.planTitle}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 1 }}>
                <Typography variant="h3" fontWeight={800}>
                  {data.monitoringPercent === null ? "—" : `${data.monitoringPercent}%`}
                </Typography>
                <Chip
                  size="small"
                  color={style.color}
                  label={isPatient ? style.patient : style.provider}
                  sx={{ fontWeight: 700 }}
                />
              </Box>
              {data.monitoringPercent !== null && (
                <LinearProgress
                  variant="determinate"
                  value={data.monitoringPercent}
                  color={style.bar}
                  sx={{ height: 8, borderRadius: 4, mb: 1 }}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                {data.monitoringExpected > 0
                  ? `${data.monitoringLogged} of ${data.monitoringExpected} expected readings logged over the last ${data.windowDays} day${data.windowDays === 1 ? "" : "s"}`
                  : data.status === "NOT_STARTED"
                  ? "This plan hasn't started yet"
                  : data.status === "NO_MONITORING"
                  ? "This plan has no readings to log"
                  : "Scoring begins once the plan has a full day behind it"}
              </Typography>
            </Grid>

            {/* Per-measurement breakdown */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="overline" color="text.secondary">
                Readings
              </Typography>
              {data.items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None required
                </Typography>
              ) : (
                data.items.map((item) => (
                  <Box
                    key={item.measurementType}
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}
                  >
                    <Typography variant="body2">
                      {MEASUREMENT_LABELS[item.measurementType] || item.measurementType}
                      {item.frequency === "WEEKLY" && (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {` · ${WEEKDAYS[item.dayOfWeek] || "weekly"}`}
                        </Typography>
                      )}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {item.dueToday && (
                        <Chip
                          size="small"
                          variant={item.doneToday ? "filled" : "outlined"}
                          color={item.doneToday ? "success" : "warning"}
                          label={item.doneToday ? "Done today" : "Due today"}
                        />
                      )}
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 36, textAlign: "right" }}>
                        {item.expected > 0 ? `${item.logged}/${item.expected}` : "—"}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Grid>

            {/* Tasks, review, last check-in */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="overline" color="text.secondary">
                Tasks &amp; review
              </Typography>
              <Typography variant="body2">
                {data.tasksTotal > 0 ? `${data.tasksDone} of ${data.tasksTotal} tasks done` : "No tasks yet"}
              </Typography>
              {data.tasksOverdue > 0 && (
                <Chip size="small" color="error" label={`${data.tasksOverdue} overdue`} sx={{ mt: 0.5, fontWeight: 700 }} />
              )}
              <Typography variant="body2" color={review.color} sx={{ mt: 1 }}>
                {review.text}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {lastCheckInText(data.lastCheckInDate, data.today)}
              </Typography>
              {!isPatient && extras > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {`${extrasLabel} logged by the patient this week (not counted in adherence)`}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Patient-only: what to do next, and where they act */}
          {isPatient && (
            <>
              {dueTodayNotDone.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {`Today: log your ${listNames(
                    dueTodayNotDone.map((i) => (MEASUREMENT_LABELS[i.measurementType] || i.measurementType).toLowerCase())
                  )}.`}
                </Alert>
              )}

              {showTasks && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      To do
                    </Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => { setError(null); setAddOpen(true); }}>
                      Add an activity
                    </Button>
                  </Box>
                  {error && !addOpen && (
                    <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}
                  {openTasks.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nothing due right now.
                    </Typography>
                  ) : (
                    openTasks.map((t) => (
                      <Box
                        key={t.id}
                        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, py: 0.75, flexWrap: "wrap" }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600} component="span">
                            {t.title}
                          </Typography>
                          {t.patientAdded && <Chip size="small" variant="outlined" label="Added by me" sx={{ ml: 1 }} />}
                          {t.dueDate && (
                            <Typography variant="caption" display="block" color={t.overdue ? "error.main" : "text.secondary"}>
                              {`${t.overdue ? "Overdue · " : "Due "}${fmtDate(t.dueDate)}`}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleOutlineIcon />}
                          disabled={busyId === t.id}
                          onClick={() => completeTask(t)}
                        >
                          Mark done
                        </Button>
                      </Box>
                    ))
                  )}
                  {extras > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                      {`${extrasLabel} logged in the last 7 days — nice! These don't change your score.`}
                    </Typography>
                  )}
                </>
              )}

              {showLink && (
                <Box sx={{ mt: 2 }}>
                  <Button size="small" variant="outlined" onClick={() => navigate(`/patients/${patientId}/my-care-plan`)}>
                    View my care plan
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {isPatient && (
        <AddActivityDialog open={addOpen} saving={saving} error={error} onClose={() => setAddOpen(false)} onSave={addActivity} />
      )}
      <Snackbar open={!!notice} autoHideDuration={3500} onClose={() => setNotice(null)}>
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      </Snackbar>
    </>
  );
}